import React, { useState, useEffect, useRef } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  Map,
  Users,
  Volume2,
  Target,
  MousePointer2,
  Maximize2,
  Activity,
  Leaf,
  UserX,
  DoorOpen
} from "lucide-react";

// --- HELPERS: HEATMAP LOGIC ---
function getHeatmapColor(intensity) {
  const gamma = 0.8;
  const adjustedIntensity = Math.min(1.0, Math.pow(intensity, gamma));
  let r, g, b;

  if (adjustedIntensity < 0.2) {
    r = Math.floor(100 + adjustedIntensity * 200);
    g = Math.floor(120 + adjustedIntensity * 50);
    b = 255;
  } else if (adjustedIntensity < 0.45) {
    const t = (adjustedIntensity - 0.2) / 0.25;
    r = Math.floor(140 * (1 - t)); g = Math.floor(160 + t * 95); b = 255;
  } else if (adjustedIntensity < 0.65) {
    const t = (adjustedIntensity - 0.45) / 0.2;
    r = 0; g = 255; b = Math.floor(255 * (1 - t));
  } else if (adjustedIntensity < 0.8) {
    const t = (adjustedIntensity - 0.65) / 0.15;
    r = Math.floor(255 * t); g = 255; b = 0;
  } else {
    const t = (adjustedIntensity - 0.8) / 0.2;
    r = 255; g = Math.floor(180 * (1 - t)); b = 0;
  }
  return { r, g, b, alpha: Math.min(250, 180 + adjustedIntensity * 80) };
}

function createLiquidNoise(x, y, t, width, height) {
  const nx = (x / width) * 2 - 1;
  const ny = (y / height) * 2 - 1;
  const distFromCenter = Math.sqrt(nx * nx + ny * ny);
  let val = (Math.sin(nx * 3 + t * 0.8) * Math.cos(ny * 3 - t * 0.6) + Math.sin(nx * 6 - t * 1.5) * Math.cos(ny * 6 + t * 1.2) * 0.5) / 1.5;
  if (distFromCenter < 0.6) val += (0.6 - distFromCenter) * 2.5;
  val += (1.0 - distFromCenter * 0.8) * 1.0 + Math.sin(nx * 5 + t) * 0.2;
  if (nx > 0.4) val -= ((nx - 0.4) / 0.6) * 3.0;
  return Math.max(0, Math.min(1, (val + 0.3) / 2.2));
}

// --- HOOKS & HELPERS ---
const DURATIONS = { LIVE_MAP: 6000, CROWD_FLOW: 5000, SOUND_FLOW: 5000 };

const useScenarioCycle = (isInView) => {
  const [activeTab, setActiveTab] = useState("Live Map");
  useEffect(() => {
    if (!isInView) return;
    let timeoutId;
    const runCycle = () => {
      if (activeTab === "Live Map") timeoutId = setTimeout(() => setActiveTab("Crowd Flow"), DURATIONS.LIVE_MAP);
      else if (activeTab === "Crowd Flow") timeoutId = setTimeout(() => setActiveTab("Sound Flow"), DURATIONS.CROWD_FLOW);
      else if (activeTab === "Sound Flow") timeoutId = setTimeout(() => setActiveTab("Live Map"), DURATIONS.SOUND_FLOW);
    };
    runCycle();
    return () => clearTimeout(timeoutId);
  }, [activeTab, isInView]);
  return activeTab;
};

// --- SIMULATION METRICS ANIMATION HOOK ---
const useSimulationMetrics = (activeTab, isInView) => {
  const [metrics, setMetrics] = useState({ deadlocks: 0, congestion: "Low", flowRate: 0 });

  useEffect(() => {
    if (!isInView || activeTab !== "Crowd Flow") {
      setMetrics({ deadlocks: 0, congestion: "Low", flowRate: 0 });
      return;
    }

    // Sequence: 0 -> 35 -> 45 -> 27 (Fast updates)
    const sequence = [
      { deadlocks: 1, congestion: "Med", flowRate: 12 },
      { deadlocks: 3, congestion: "High", flowRate: 35 },
      { deadlocks: 5, congestion: "Critical", flowRate: 45 },
      { deadlocks: 2, congestion: "Med", flowRate: 27 },
      { deadlocks: 0, congestion: "Low", flowRate: 22 }
    ];

    let step = 0;
    const interval = setInterval(() => {
      if (step < sequence.length) {
        setMetrics(sequence[step]);
        step++;
      }
    }, 800); // Update every 800ms

    return () => clearInterval(interval);
  }, [activeTab, isInView]);

  return metrics;
};

const useCursorAnimation = (activeTab, isInView) => {
  const startPos = { x: 35, y: 60 };
  const boothStart = { x: 27, y: 30, w: 8, h: 14 };
  const boothEnd = { x: 78, y: 12, w: 8, h: 14 };

  const [cursor, setCursor] = useState({ ...startPos, active: false });
  const [booth, setBooth] = useState({ ...boothStart });
  const [phase, setPhase] = useState("idle");

  useEffect(() => {
    if (!isInView) return;

    let frameId;
    let startTime = performance.now();

    const animate = (time) => {
      const t = (time - startTime) / 1000;
      const easeInOutQuad = (x) => x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

      let newCursor = { ...cursor };
      let newBooth = { ...booth };
      let currentPhase = "idle";

      if (activeTab === "Live Map") {
        if (t < 0.8) { // Seek to Booth
          const p = t / 0.8;
          newCursor.x = startPos.x + (boothStart.x + 4 - startPos.x) * easeInOutQuad(p);
          newCursor.y = startPos.y + (boothStart.y + 7 - startPos.y) * easeInOutQuad(p);
          newBooth = { ...boothStart };
        } else if (t < 1.0) { // Grab
          currentPhase = "grabbing";
          newCursor = { x: boothStart.x + 4, y: boothStart.y + 7, active: true };
          newBooth = { ...boothStart };
        } else if (t < 3.5) { // Drag to Wall
          currentPhase = "dragging";
          const p = (t - 1.0) / 2.5;
          const ease = 1 - Math.pow(1 - p, 2.5);
          newBooth.x = boothStart.x + (boothEnd.x - boothStart.x) * ease;
          newBooth.y = boothStart.y + (boothEnd.y - boothStart.y) * ease;
          newCursor = { x: newBooth.x + 4, y: newBooth.y + 10, active: true };
        } else { // Release & Drift (Violation)
          currentPhase = "violation";
          newBooth = { ...boothEnd };
          const p = Math.min(1, Math.max(0, (t - 3.5) * 1.5));
          const ease = easeInOutQuad(p);
          // Drift slightly away from booth after dropping
          newCursor = {
            x: (boothEnd.x + 4) + 5 * ease,
            y: (boothEnd.y + 7) + 5 * ease,
            active: false
          };
        }
      } else if (activeTab === "Crowd Flow") {
        // Start exactly where Live Map left off (boothEnd + 5 offset)
        const startCX = (boothEnd.x + 4) + 5;
        const startCY = (boothEnd.y + 7) + 5;

        if (t < 0.5) { // 1. Move Cursor back to Booth Center
          currentPhase = "seeking-reset";
          const p = t / 0.5;
          const ease = easeInOutQuad(p);
          newCursor.x = startCX + ((boothEnd.x + 4) - startCX) * ease;
          newCursor.y = startCY + ((boothEnd.y + 7) - startCY) * ease;
          newCursor.active = false; // Not grabbing yet
          newBooth = { ...boothEnd };
        } else if (t < 2.5) { // 2. Grab & Reset Booth Position
          currentPhase = "resetting";
          const p = (t - 0.5) / 2.0;
          const ease = easeInOutQuad(p);
          newBooth.x = boothEnd.x + (boothStart.x - boothEnd.x) * ease;
          newBooth.y = boothEnd.y + (boothStart.y - boothEnd.y) * ease;
          newCursor = { x: newBooth.x + 4, y: newBooth.y + 7, active: true };
        } else { // 3. Release & Return to Idle
          newBooth = { ...boothStart };
          const p = Math.min(1, (t - 2.5) / 1.0);
          const ease = easeInOutQuad(p);
          newCursor.x = (boothStart.x + 4) + (startPos.x - (boothStart.x + 4)) * ease;
          newCursor.y = (boothStart.y + 7) + (startPos.y - (boothStart.y + 7)) * ease;
          newCursor.active = false;
        }
      } else {
        newBooth = { ...boothStart };
        // Idle Drift
        const drift = Math.sin(t) * 2;
        newCursor = { x: startPos.x + drift, y: startPos.y + drift * 0.5, active: false };
      }

      setPhase(currentPhase);
      setCursor(newCursor);
      setBooth(newBooth);
      frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [activeTab, isInView]);

  return { cursor, booth, phase };
};

const CrowdHeatmap = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const W = 80, H = 45;
    canvasRef.current.width = W; canvasRef.current.height = H;
    let start = performance.now();
    let frame;
    const render = (t) => {
      const time = (t - start) / 1000;
      const img = ctx.createImageData(W, H);
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const { r, g, b, alpha } = getHeatmapColor(createLiquidNoise(x, y, time, W, H));
          const i = (y * W + x) * 4;
          img.data[i] = r; img.data[i + 1] = g; img.data[i + 2] = b; img.data[i + 3] = alpha;
        }
      }
      ctx.putImageData(img, 0, 0);
      frame = requestAnimationFrame(render);
    };
    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none blur-2xl opacity-60" />;
};

const SoundWaveMap = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    // 1. RESOLUTION: Increased to 160x90 (from 80x45)
    // This removes the "blocky/dust" look while still being fast.
    const W = 160, H = 90;
    canvasRef.current.width = W;
    canvasRef.current.height = H;

    // Position source (needs to scale with new W/H)
    const originX = W * 0.448;
    const originY = H * 0.6;

    let start = performance.now();
    let frame;

    const render = (t) => {
      const time = (t - start) / 1000;
      const img = ctx.createImageData(W, H);

      // Radius: Still kept small as requested
      const maxRadius = H * 0.35;

      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          const nx = x - originX;
          const ny = y - originY;
          const dist = Math.sqrt(nx * nx + ny * ny);

          if (dist > maxRadius) {
            // Transparent if outside radius
            const i = (y * W + x) * 4;
            img.data[i + 3] = 0;
            continue;
          }

          const distFactor = dist / maxRadius;

          // 2. WAVE SHAPE: Thicker and smoother
          // Lowered frequency (1.5) creates wider bands (less "nervous")
          // Reduced speed (3) for a calm ripple
          const wave = Math.sin(dist * 0.8 - time * 6);

          // Smoother Pulse: Removed the high power (Math.pow).
          // Just shifting sine to 0-1 range makes thick, connected rings.
          const intensity = (wave + 1) / 2;

          // 3. COLOR: Red -> Blue
          const r = 255 * (1 - distFactor);
          const b = 100 + (155 * distFactor);
          const g = 0;

          // 4. FALLOFF: Smooth Fade
          // We simply fade opacity based on distance. 
          // intensity * 255 gives us the base wave visibility.
          // (1 - distFactor) makes it fade out as it gets to the edge.
          const alpha = intensity * 230 * (1 - distFactor);

          const i = (y * W + x) * 4;
          img.data[i] = r;
          img.data[i + 1] = g;
          img.data[i + 2] = b;
          img.data[i + 3] = alpha;
        }
      }

      ctx.putImageData(img, 0, 0);
      frame = requestAnimationFrame(render);
    };

    frame = requestAnimationFrame(render);
    return () => cancelAnimationFrame(frame);
  }, []);

  // 'blur-sm' is enough now that we have higher resolution
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none opacity-90 blur-sm mix-blend-plus-lighter" />;
};

const LogItem = ({ icon, type, msg, status }) => {
  let colorClass, bgClass;
  if (status === 'warning') { colorClass = 'text-orange-500'; bgClass = 'text-orange-500'; }
  else if (status === 'critical') { colorClass = 'text-red-500'; bgClass = 'text-red-500'; }
  else { colorClass = 'text-green-600'; bgClass = 'text-green-500'; }

  return (
    <div className="px-4 py-3 border-b border-[#F0F0F0] flex gap-3 hover:bg-gray-50 transition-colors">
      <div className={`mt-0.5 ${bgClass}`}>{icon}</div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[9px] font-bold font-mono ${colorClass}`}>{type}</span>
        </div>
        <p className="text-[11px] leading-tight font-medium text-[#333]">{msg}</p>
      </div>
    </div>
  );
};

// --- ANIMATED COUNTER ---
const AnimatedCounter = ({ value, duration = 600 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTimestamp = null;
    const start = displayValue;
    const end = value;

    if (start === end) return;

    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);

      // Linear interpolation for smooth counting
      setDisplayValue(Math.floor(progress * (end - start) + start));

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };

    window.requestAnimationFrame(step);
  }, [value]);

  return <>{displayValue}</>;
};

// --- MAIN COMPONENT ---
const FeaturesSection = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.3 } // Trigger when 30% visible
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeTab = useScenarioCycle(isInView);
  const containerRef = useRef(null); // Keep this for inner ref usage if needed
  const { cursor, booth, phase } = useCursorAnimation(activeTab, isInView);
  const simMetrics = useSimulationMetrics(activeTab, isInView); // Get dynamic metrics

  const WALL_X_PERCENT = 80;
  const MAP_WIDTH_METERS = 50;

  const safeBoothX = booth.x || 26;
  const safeBoothW = booth.w || 8;
  const safeBoothH = booth.h || 14;

  const boothRightEdge = safeBoothX + safeBoothW;
  const boothCenterY = booth.y + safeBoothH / 2;

  const distancePercent = Math.max(0, WALL_X_PERCENT - boothRightEdge);
  const distanceMetersVal = (distancePercent / 100) * MAP_WIDTH_METERS;
  const distanceMetersStr = isNaN(distanceMetersVal) ? "5.00" : distanceMetersVal.toFixed(2);
  const isViolation = distanceMetersVal < 3.0;

  // Disable transition during drag/reset to prevent lag
  const boothTransition = (phase === 'dragging' || phase === 'resetting') ? 'transition-none' : 'transition-all duration-300 ease-in-out';

  return (
    <section ref={sectionRef} className="relative w-full py-24 px-4 lg:px-8 bg-white border-b border-[#E0E0E0] overflow-hidden">

      {/* HERO CONTENT */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8 mb-10 lg:mb-24">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-[1px] w-8 bg-orange-600"></div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
            Health, Safety, Security, and Environment
          </span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <h2 className="font-sans font-bold text-[44px] md:text-[56px] lg:text-[64px] leading-[0.95] text-black tracking-tighter uppercase text-balance">
            Human error is <br className="hidden lg:block" />
            the only risk <br className="hidden lg:block" /> you&nbsp;<span className="text-[#FF5252] font-mono tracking-[-0.06em]">can<span className="tracking-[-0.2em]">'</span>t</span>&nbsp;afford
          </h2>

          <p className="font-sans text-[20px] md:text-[24px] leading-[1.3] text-zinc-800 max-w-[500px] lg:text-right lg:pb-2 text-balance">
            Our AI engine scans your floor plan, catching hazards before they become&nbsp;liabilities.
          </p>
        </div>
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="relative w-full bg-white border border-[#E0E0E0] shadow-2xl rounded-xl overflow-hidden flex flex-col">

          {/* --- LEFT: MAP --- */}
          <div className="relative w-full md:w-[calc(100%-20rem)] bg-white overflow-hidden flex flex-col">

            {/* TABS */}
            <div className="h-12 bg-white border-b border-[#E0E0E0] flex items-center px-2 z-20 pointer-events-none">
              {["Live Map", "Crowd Flow", "Sound Flow"].map(tab => (
                <div
                  key={tab}
                  className={`flex items-center gap-2 px-4 h-full text-[11px] font-mono font-bold uppercase tracking-wider border-b-2 transition-all duration-500 ease-in-out ${activeTab === tab ? "text-black border-black bg-gray-50" : "text-gray-400 border-transparent"}`}
                >
                  {tab === "Live Map" ? <Map size={14} className="relative z-10" /> : tab === "Crowd Flow" ? <Users size={14} className="relative z-10" /> : <Volume2 size={14} className="relative z-10" />}
                  <span className="relative z-10">{tab}</span>
                </div>
              ))}
            </div>

            {/* STAGE */}
            <div className="relative w-full bg-white flex items-center justify-center">
              <div ref={containerRef} className="relative w-full aspect-[16/9] select-none">

                {/* LAYERS */}
                <div className="absolute inset-0 z-0 pointer-events-none opacity-5 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:44px_44px]" />
                <div className={`absolute inset-0 z-10 transition-opacity duration-1000 ${activeTab === 'Crowd Flow' ? 'opacity-100' : 'opacity-0'}`}><CrowdHeatmap /></div>
                <div className={`absolute inset-0 z-10 transition-opacity duration-1000 ${activeTab === 'Sound Flow' ? 'opacity-100' : 'opacity-0'}`}><SoundWaveMap /></div>
                <img
                  src="/assets/floorplan.png"
                  alt="Floor"
                  className="absolute z-20 bottom-0 left-[11%] w-[75%] h-[100%] object-cover pointer-events-none"
                />

                {/* NO BUILD ZONE (WALL) */}
                <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-500/50 bg-red-500/5 z-10" style={{ left: `${WALL_X_PERCENT}%`, right: 0 }}>
                  <div className="absolute top-1/2 -translate-y-1/2 -left-6 -rotate-90 text-red-600 text-[10px] font-mono font-bold whitespace-nowrap">NO BUILD ZONE</div>
                </div>

                {/* DYNAMIC LINE (The "Tape Measure") */}
                {activeTab === 'Live Map' && (
                  <div className="absolute z-30 pointer-events-none" style={{ left: 0, top: 0, width: '100%', height: '100%' }}>
                    <div
                      className="absolute border-t-[2px] pt-1 flex items-center justify-center transition-colors duration-75"
                      style={{
                        left: `${boothRightEdge}%`,
                        top: `${boothCenterY}%`,
                        width: `${distancePercent}%`,
                        height: '0px',
                        borderColor: isViolation ? '#FF5252' : '#1A1A1A',
                        borderStyle: isViolation ? 'solid' : 'dashed'
                      }}
                    >
                      <div className={`absolute left-0 w-2 h-2 rounded-full -translate-x-1/2 -translate-y-1/2 ${isViolation ? 'bg-[#FF5252]' : 'bg-black'}`}></div>
                      <div className={`absolute right-0 w-2 h-2 rounded-full translate-x-1/2 -translate-y-1/2 ${isViolation ? 'bg-[#FF5252]' : 'bg-black'}`}></div>
                      <div className={`absolute -top-7 px-2 py-1 text-[11px] font-mono font-bold rounded border shadow-sm ${isViolation ? 'bg-[#FF5252] text-white border-[#FF5252]' : 'bg-white text-black border-black'}`}>
                        {distanceMetersStr}m
                      </div>
                    </div>
                  </div>
                )}

                {/* BOOTH A-04 */}
                <div
                  className={`absolute flex flex-col items-center justify-center border z-20 ${boothTransition} ${isViolation && activeTab === 'Live Map' ? 'bg-red-500/90 border-red-600 shadow-[0_0_20px_rgba(255,82,82,0.8)] text-white' : 'bg-white border border-gray-300 text-black'}`}
                  style={{ left: `${booth.x}%`, top: `${booth.y}%`, width: `${booth.w}%`, height: `${booth.h}%` }}
                >
                  <span className="font-sans text-[10px] lg:text-[16px] font-medium">A-04</span>
                </div>

                {/* CURSOR */}
                <div className={`absolute z-50 transition-opacity duration-200 ${activeTab !== 'Sound Flow' ? 'opacity-100' : 'opacity-0'}`} style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }}>
                  <MousePointer2 size={24} className="fill-black stroke-white stroke-2 -translate-x-1 -translate-y-1" />
                </div>

              </div>
            </div>
          </div>

          {/* --- RIGHT: LOGS --- */}
          <div className="w-full md:w-80 bg-white border-l border-[#E0E0E0] flex flex-col z-20 shrink-0 h-[300px] md:absolute md:top-0 md:right-0 md:bottom-0 md:h-auto">
            <div className="p-4 border-b border-[#E0E0E0] bg-[#FAFAFA] flex items-center justify-between">
              <h4 className="font-mono text-xs font-bold text-[#999] tracking-wider">HSSE_LOGS</h4>
            </div>

            <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">

              {/* 1. VIOLATION ALERT (Live Map) */}
              <div className={`transition-all duration-300 overflow-hidden bg-red-50 border-b border-red-100 ${activeTab === 'Live Map' && isViolation ? 'max-h-40 opacity-100 py-4' : 'max-h-0 opacity-0 py-0'}`}>
                <div className="px-4 flex gap-3">
                  <div className="mt-0.5 text-red-600 p-1 rounded-full"><AlertTriangle size={14} /></div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-red-600 font-mono">VIOLATION</span>
                    </div>
                    <p className="text-[12px] font-semibold text-[#1A1A1A] leading-tight">Min object distance from wall required 3m</p>
                    <p className="text-[10px] text-red-600 font-mono mt-1">Current: {distanceMetersStr}m</p>
                  </div>
                </div>
              </div>

              {/* 2. CROWD SIMULATION STATS (Crowd Flow) - DYNAMIC NUMBERS */}
              <div className={`transition-all duration-300 overflow-hidden bg-blue-50 border-b border-blue-100 ${activeTab === 'Crowd Flow' ? 'max-h-48 opacity-100 py-4' : 'max-h-0 opacity-0 py-0'}`}>
                <div className="px-4 flex gap-3">
                  <div className="mt-0.5 text-blue-600 p-1 rounded-full"><Activity size={14} /></div>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-blue-600 font-mono">SIMULATION_METRICS</span>
                    </div>

                    <div className="grid grid-cols-3 lg:grid-cols-2 gap-2 mt-2">
                      {/* Deadlocks */}
                      <div className="rounded">
                        <span className="block text-[9px] text-gray-500 font-mono uppercase">Deadlocks</span>
                        <span className="block text-[14px] font-semibold text-gray-700 font-mono">{simMetrics.deadlocks}</span>
                      </div>

                      {/* Congestion */}
                      <div className="rounded">
                        <span className="block text-[9px] text-gray-500 font-mono uppercase">Congestion</span>
                        <span className={`block text-[14px] font-semibold font-mono ${simMetrics.congestion === 'Critical' ? 'text-red-500' : 'text-orange-500'}`}>
                          {simMetrics.congestion}
                        </span>
                      </div>

                      {/* MOBILE ONLY: Flow Rate (Combined) */}
                      <div className="rounded lg:hidden">
                        <span className="block text-[9px] text-gray-500 font-mono uppercase">Flow Rate</span>
                        <span className="text-[12px] text-[#1A1A1A] font-mono">
                          <span className="font-bold"><AnimatedCounter value={simMetrics.flowRate} /></span>
                          <span className="ml-1 font-normal text-gray-500">ppl/min</span>
                        </span>
                      </div>

                      {/* DESKTOP ONLY: Grid Split */}
                      {/* Row 2 Col 1: Label */}
                      <div className="hidden lg:block pt-1">
                        <span className="block text-[9px] text-gray-500 font-mono uppercase">Flow Rate</span>
                      </div>

                      {/* Row 2 Col 2: Value */}
                      <div className="hidden lg:block">
                        <span className="text-[12px] text-[#1A1A1A] font-mono">
                          <span className="font-bold"><AnimatedCounter value={simMetrics.flowRate} /></span>
                          <span className="ml-1 font-normal text-gray-500">ppl/min</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. SOUND FLOW ALERT (Sound Flow) */}
              <div className={`transition-all duration-300 overflow-hidden bg-purple-50 border-b border-purple-100 ${activeTab === 'Sound Flow' ? 'max-h-40 opacity-100 py-4' : 'max-h-0 opacity-0 py-0'}`}>
                <div className="px-4 flex gap-3">
                  <div className="mt-0.5 text-purple-600 p-1 rounded-full"><Volume2 size={14} /></div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-purple-600 font-mono">NOISE POLLUTION</span>
                    </div>
                    <p className="text-[11px] font-bold text-[#1A1A1A] leading-tight">Booths P48, P49 heavily affected (&gt; 85dB)</p>
                  </div>
                </div>
              </div>

              {/* STATIC LOGS (With new requests) */}
              <div className="opacity-80">
                <LogItem icon={<DoorOpen size={14} />} type="EXIT_CHECK" msg="Emergency exit E-02 clearance reduced to 1.4m (Min: 2.0m)" status="critical" />
                <LogItem icon={<AlertTriangle size={14} />} type="HAZARD" msg="Temporary structure intersects primary pedestrian flow" status="warning" />
                <LogItem icon={<UserX size={14} />} type="STAFFING" msg="Zone coverage vs expected staffing: GAP" status="warning" />
                <LogItem icon={<Leaf size={14} />} type="CARBON" msg="Est. Carbon Footprint: 450kg CO2e (Optimized)" status="ok" />
                <LogItem icon={<Target size={14} />} type="ZONING" msg="Booth placement verification initialized." status="ok" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;